import {
  BoxProps,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CloseIcon } from 'theme/overrides/CustomIcons';
import palette from 'theme/palette';
import { loadAsyncJs } from 'utils/loadAsync';
import Iconify from './Iconify';
import Shareable from './Shareable';

interface Props extends BoxProps {
  formId: string;
  formUpdated: number;
}
declare global {
  interface Window {
    formPayId: string;
    FormPay: any;
  }
}

const RootStyle = styled('div')`
  .preview-form {
    /* pointer-events: none; */
  }

  .preview-title {
    display: flex;
    flex-direction: row;
    padding: 10px;
  }
  .actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .actions-button {
    cursor: pointer;
    margin-left: 10px;
    height: 20px;
    color: ${palette.dark.primary.main};
    display: flex;
    align-items: center;
    :hover {
      color: ${palette.dark.primary.dark};
    }
  }
`;

function FormEditorSetting({ formId, formUpdated }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  window.formPayId = formId;
  const [open, setOpen] = useState<boolean | 'Embed' | 'Share'>(false);
  const handleOpenClose = () => {
    setOpen(!open);
  };

  const { formDetails } = useSelector((state: any) => state.formEditor);

  useEffect(() => {
    if (ref.current) {
      ref.current
        .parentNode!.querySelector('#fp-all-container-' + formId)
        ?.remove();

      ref.current.parentNode!.querySelector('.fp-pay-now')?.remove();

      sessionStorage.removeItem('paymentToken');

      if (!window.FormPay) {
        loadAsyncJs(
          process.env.REACT_APP_FORMPAY_SCRIPT_URL || '/main-widget/bundle.js'
        ).then(() => {
          new window.FormPay(window.formPayId, ref.current, {
            payNow: formDetails?.payButton,
            allowStyling: true,
          }).initForm();
        });
      } else {
        new window.FormPay(window.formPayId, ref.current, {
          payNow: formDetails?.payButton,
          allowStyling: true,
        }).initForm();
      }
    }
  }, [formId, formUpdated]);

  const embedCodeClick = () => {
    setOpen('Embed');
  };
  const shareLinkClick = () => {
    setOpen('Share');
  };

  const embedableCode = `<script src='${
    process.env.REACT_APP_FORMPAY_SCRIPT_URL
  }'></script>
<script type='text/javascript'  data-name="fp" ${
    formDetails?.payButton ? 'data-pay-now="pay-now"' : ''
  }>
  FormPay.init('${formId}');
</script>`;

  const shareLink = 'https://' + window.location.host + '/share/' + formId;

  return (
    <Grid item xs={12} md={6}>
      <RootStyle>
        <div className='preview-title'>
          <div className='actions'>
            <span className='actions-button' onClick={shareLinkClick}>
              <Iconify icon={'eva:share-fill'} />
              {t('share_url')}
            </span>
            <span className='actions-button' onClick={embedCodeClick}>
              <Iconify icon={'material-symbols:content-copy-outline'} />
              {t('embed_code')}
            </span>
          </div>
        </div>

        <Stack className='preview-form'>
          <div
            style={{
              margin: '10px 20px 10px 20px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div ref={ref} />
          </div>
        </Stack>
        <Dialog
          onClose={handleOpenClose}
          open={open !== false}
          maxWidth='md'
          fullWidth={true}
        >
          <DialogTitle>
            {t('your_form_is_published')}{' '}
            {handleOpenClose ? (
              <IconButton
                aria-label='close'
                onClick={handleOpenClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: theme => `${theme.palette.primary.lighter}`,
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
          <Shareable
            handleOpenClose={handleOpenClose}
            embedCode={embedableCode}
            shareLink={shareLink}
            type={open as 'Embed' | 'Share'}
          />
        </Dialog>
      </RootStyle>
    </Grid>
  );
}

export default React.memo(FormEditorSetting);
